import {
    Bar,
    BarChart as RCBarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

/**
 * One series to plot as a set of bars. `label` is what shows in the legend
 * and tooltip; `dataKey` is the field read off each row in `data`.
 */
export interface BarSeries {
    dataKey: string;
    label: string;
}

export interface BarChartProps<T extends Record<string, unknown>> {
    data: T[];
    /** The category field each bar group is keyed by (e.g. "month", "day"). */
    xAxisKey: string;
    /** One series renders a single bar per category; more render side by side (or stacked, see `stacked`). */
    bars: BarSeries[];
    /** Stack all series into a single bar per category instead of placing them side by side. */
    stacked?: boolean;
    /**
     * Color each bar by its category instead of by series. Only meaningful with a single series —
     * use for distinct categories (e.g. engagement buckets), not for the same metric over time
     * (e.g. actions per day), which should read as one color.
     */
    colorByCategory?: boolean;
    height?: number;
}

const axisTick = { fill: "var(--muted-foreground)", fontSize: 11 };
const BAR_COLORS = [
    "#247BA0",
    "#F25F5C",
    "#7570b3",
    "#70C1B3",
    "#e6ab02",
    "#66a61e",
];
const BAR_RADIUS: [number, number, number, number] = [
    4,
    4,
    0,
    0,
];

/** Bar chart for one or more series over a shared category axis (e.g. month, day), grouped or stacked. */
export function BarChart<T extends Record<string, unknown>>({
    data,
    xAxisKey,
    bars,
    stacked = false,
    colorByCategory = false,
    height = 260,
}: BarChartProps<T>) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <RCBarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey={xAxisKey} tick={axisTick} stroke="var(--border)" />
                <YAxis tick={axisTick} stroke="var(--border)" width={40} allowDecimals={false} />
                <Tooltip
                    contentStyle={{
                        background: "var(--background)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                    }}
                />
                {bars.length > 1 && <Legend />}
                {bars.map((bar, index) => (
                    <Bar
                        key={bar.dataKey}
                        dataKey={bar.dataKey}
                        name={bar.label}
                        fill={BAR_COLORS[index % BAR_COLORS.length]}
                        stackId={stacked ? "stack" : undefined}
                        radius={!stacked || index === bars.length - 1 ? BAR_RADIUS : undefined}
                    >
                        {colorByCategory &&
                            bars.length === 1 &&
                            data.map((_, dataIndex) => (
                                <Cell key={dataIndex} fill={BAR_COLORS[dataIndex % BAR_COLORS.length]} />
                            ))}
                    </Bar>
                ))}
            </RCBarChart>
        </ResponsiveContainer>
    );
}
